import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// visx
import { ParentSize } from '@visx/responsive';
import { scaleTime, scaleLinear } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { bisector } from 'd3-array';
import styles from './area-chart.module.scss';

// visx tooltip
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';

// d3-shape
import { area, curveMonotoneX } from 'd3-shape';

// react-spring
import { useSpring, animated } from 'react-spring';
import { formatDate } from '../helpers';
import { DateTime } from 'luxon';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useLocation } from 'react-router-dom';
import { ChartSkeleton } from '@flexo/atoms';
import { IconWrapper } from '@flexo/general';

/** Replace with your own date formatting logic. */
const ENV = (import.meta as any).env;
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isWebSafari = ENV?.VITE_APP_IS_WEB === 'true' && isSafari;

const browserLanguage = navigator.language;

/**
 * Consolidated chart configuration object that replaces scattered magic numbers
 * throughout the component. This provides:
 *
 * 1. Platform-specific adjustments (web vs mobile)
 * 2. Chart dimension calculations
 * 3. Interaction settings
 * 4. Axis positioning adjustments
 *
 * Benefits:
 * - All magic numbers are documented and centralized
 * - Consistent platform-specific behavior
 * - Easy to maintain and modify dimensions
 * - Clear separation of concerns
 */
const CHART_CONFIG = {
  // Platform-specific adjustments
  web: {
    containerPadding: 45, // Padding applied to container width
    containerHeight: 255, // Chart drawing area height
    svgHeight: 275, // Total SVG height (containerHeight + header space)
    headerMarginTop: 60, // Space for chart header/legend
  },
  mobile: {
    containerPadding: 0, // No padding on mobile (full width)
    containerHeight: 200, // Smaller chart area for mobile
    svgHeight: 220, // Total SVG height for mobile
    headerMarginTop: 40, // Same header space as web
  },
  // Chart dimensions
  chartPadding: {
    right: ENV?.VITE_APP_IS_WEB === 'true' ? 0 : 16, // Extra padding for chart area beyond left margin
    axisExtension: 20, // Extension for chart drawing area beyond axis
  },
  // Interaction and display
  interaction: {
    overlayPadding: { width: 100, height: 50 }, // Extra interaction area
    minVibrationInterval: 50, // Minimum time between haptic feedback
  },
  // Axis positioning adjustments
  axis: {
    bottomLeftOffset: -10, // AxisBottom left position adjustment
    yPadding: 10, // Y-axis padding from top/bottom edges
  },
};

/**
 * Animated Area Component with consistent scaling
 */
function AnimatedAreaClosed<T>({
  data,
  xScale,
  yScale,
  accessors,
  fill,
  stroke,
  fillOpacity,
  curve = curveMonotoneX,
  originalData,
  allKeys,
}: {
  data: T[];
  xScale: any;
  yScale: any;
  accessors: {
    x: (d: T) => number;
    y: (d: T) => number;
  };
  fill?: string;
  stroke?: string;
  fillOpacity?: number;
  curve?: any;
  originalData?: any[];
  allKeys?: string[];
  }) {
  

  // Process data to add sharp cut at the end
  const processedData = useMemo(() => {
    if (!data || !data.length) return data;

    // Find the last point with meaningful (non-zero, non-null) data for sharp cut
    let lastMeaningfulIndex = -1;
    for (let i = data.length - 1; i >= 0; i--) {
      const yValue = accessors.y(data[i]);
      if (yValue !== null && yValue !== undefined && yValue !== 0) {
        lastMeaningfulIndex = i;
        break;
      }
    }

    // If no meaningful data found, return original data
    if (lastMeaningfulIndex === -1) return data;

    // Check if we should create sharp cut by examining the next point in original data
    let shouldCreateSharpCut = false;

    if (originalData && allKeys && lastMeaningfulIndex >= 0) {
      const nextDataIndex = lastMeaningfulIndex + 1;

      // Check if there's a next data point
      if (nextDataIndex < originalData.length) {
        const nextPoint = originalData[nextDataIndex];

        // Check if all values (except date) in the next point are null (0 is valid, not null)
        const allValuesNull = allKeys.every(
          (key) => nextPoint[key] === null || nextPoint[key] === undefined
        );

        shouldCreateSharpCut = allValuesNull;
      } else {
        // If there's no next point, create sharp cut (end of data)
        shouldCreateSharpCut = true;
      }
    } else {
      // Fallback to original behavior if we don't have the required data
      shouldCreateSharpCut = true;
    }

    // If we should NOT create a sharp cut (values transition to zero, not null)
    // Return all data to allow natural sloping to zero
    if (!shouldCreateSharpCut) {
      return data;
    }

    // For sharp cut case: truncate data to last meaningful point and add extension
    const truncatedData = data.slice(0, lastMeaningfulIndex + 1);

    if (!truncatedData.length) return truncatedData;

    const lastPoint = truncatedData[truncatedData.length - 1];
    const lastY = accessors.y(lastPoint);

    // If the last point has no value (null), return truncated data
    if (lastY === null || lastY === undefined) {
      return truncatedData;
    }

    // Create an additional point slightly to the right with the same Y value
    // This will create a horizontal extension that then drops sharply
    const lastDate = new Date((lastPoint as any).date);
    const timeIncrement = 1000 * 60 * 15; // 15 minutes ahead
    const extendedDate = new Date(lastDate.getTime() + timeIncrement);

    const extendedPoint = {
      ...lastPoint,
      date: extendedDate.toISOString(),
    } as T;

    return [...truncatedData, extendedPoint];
  }, [data, accessors, originalData, allKeys]);

  // Current area path
  const pathString = useMemo(() => {
    if (!processedData || !processedData.length) return 'M0,0';

    const areaGenerator = area<T>()
      .x((d) => xScale(accessors.x(d)))
      .y0(() => yScale(0))
      .y1((d) => yScale(accessors.y(d)))
      .curve(curve);

    return areaGenerator(processedData) || 'M0,0';
  }, [processedData, xScale, yScale, accessors, curve]);

  // Baseline path (for animation from zero)
  const zeroPathString = useMemo(() => {
    if (!processedData || !processedData.length) return 'M0,0';

    const areaGenerator = area<T>()
      .x((d) => xScale(accessors.x(d)))
      .y0(() => yScale(0))
      .y1(() => yScale(0))
      .curve(curve);

    return areaGenerator(processedData) || 'M0,0';
  }, [processedData, xScale, yScale, accessors, curve]);

  const [springs, api] = useSpring(() => ({
    from: { path: zeroPathString },
    to: { path: pathString },
    config: { tension: 170, friction: 26 },
  }));

  useEffect(() => {
    api.start({
      from: { path: zeroPathString },
      to: { path: pathString },
      config: { tension: 170, friction: 26 },
    });
  }, [api, zeroPathString, pathString]);

  const AnimatedPath = animated('path');

  return (
    <AnimatedPath
      d={springs.path}
      fill={fill}
      fillOpacity={fillOpacity}
      stroke={stroke}
    />
  );
}

/**
 * Clean Area Chart Component
 */
interface DataPoint {
  date: string;
  [key: string]: any;
}

export const NewAreaChart = ({
  colors = [],
  data = [] as DataPoint[],
  keys = [] as string[],
  title = '',
  unit = '',
  margin = { top: 10, right: 0, bottom: 30, left:  ENV?.VITE_APP_IS_WEB === 'true' ? 35 : 25 },
  containerWidth,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  // Redux
  const calendar = useSelector((state: any) => state.calendar);
  const { selectedTimeSet } = calendar?.hiveCalendar || {
    selectedTimeSet: 'day',
  };
  const kpi = useSelector((state: any) => state.kpi);

  // Determine entity and context from URL
  const pathSegment = location.pathname.split('/')[1];
  let entity = 'Site';
  let kpiContext = 'Energy';

  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }
  if (pathSegment?.includes('community')) {
    entity = 'Community';
  }
  if (pathSegment?.includes('member')) {
    entity = 'Member';
  }

  const LOADING_STATE = kpi?.[`${kpiContext}KpiData${entity}AsyncStatus`];

  // State management
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>({});
  const [selectedData, setSelectedData] = useState<any | null>(null);
  const [interpolatedData, setInterpolatedData] = useState<any | null>(null);
  const [interacting, setInteracting] = useState(false);
  const [width, setWidth] = useState(containerWidth);
  const [dataLoadCounter, setDataLoadCounter] = useState(0);

  // Platform-specific configuration
  const isWeb = ENV?.VITE_APP_IS_WEB === 'true';
  const platformConfig = isWeb ? CHART_CONFIG.web : CHART_CONFIG.mobile;

  // Refs for performance optimization
  const lastHoveredDateRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastVibrationTimeRef = useRef(0);

  // Tooltip setup
  const { showTooltip, hideTooltip, tooltipOpen } = useTooltip<any>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal();

  // Process and sort data
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort(
      (a, b) =>
        new Date((a as any).date).getTime() -
        new Date((b as any).date).getTime()
    );
  }, [data]);

  // Find the last point with meaningful data
  const lastMeaningfulDataPoint: DataPoint | null = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return null;

    let lastPoint: DataPoint | null = null;
    for (let i = sortedData.length - 1; i >= 0; i--) {
      if (
        keys.some(
          (key) =>
            visibleKeys[key] &&
            sortedData[i][key] != null &&
            sortedData[i][key] !== 0
        )
      ) {
        lastPoint = sortedData[i];
        break;
      }
    }
    return lastPoint;
  }, [sortedData, keys, visibleKeys]);

  // Check if all data is null/undefined (no data available)
  const isNullData = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return true;
    return sortedData.every((d) =>
      keys.every((key) => d[key] === null || d[key] === undefined)
    );
  }, [sortedData, keys]);

  // Check if all data is zero (or combination of 0 and null)
  const is0ValueData = useMemo(() => {
    if (isNullData || !sortedData || sortedData.length === 0) return false; // If it's null data or no data, it's not 0 value data
    return sortedData.every((d) =>
      keys.every(
        (key) => d[key] === 0 || d[key] === null || d[key] === undefined
      )
    );
  }, [sortedData, keys, isNullData]);

  // Calculate dimensions using configuration
  const innerWidth = width - margin.left - CHART_CONFIG.chartPadding.right;
  const containerHeight = platformConfig.containerHeight;

  // Create unified time scale (extended for chart area)
  const xScale = useMemo(() => {
    if (!sortedData.length)
      return scaleTime({
        domain: [0, 1],
        range: [
          0,
          innerWidth + margin.left + CHART_CONFIG.chartPadding.axisExtension,
        ],
      });

    const dates = sortedData.map((d) => new Date(d.date));
    const domain = [dates[0], dates[dates.length - 1]];

    return scaleTime<number>({
      domain,
      range: [
        margin.left,
        innerWidth + margin.left + CHART_CONFIG.chartPadding.axisExtension,
      ],
    });
  }, [sortedData, width, margin.left, innerWidth]);

  // Create separate scale for the bottom axis (aligned with visible axis)
  const xAxisScale = useMemo(() => {
    if (!sortedData.length)
      return scaleTime({
        domain: [0, 1],
        range: [0, innerWidth],
        nice: true,
      });

    const dates = sortedData.map((d) => new Date(d.date));
    const domain = [dates[0], dates[dates.length - 1]];

    return scaleTime<number>({
      domain,
      range: [margin.left, innerWidth + margin.left],
    });
  }, [sortedData, innerWidth, margin.left]);

  // Y scale
  const yScale = useMemo(() => {
    let minVal = 0;
    let maxVal = 0;
    let hasData = false;

    sortedData.forEach((row) => {
      keys.forEach((k) => {
        if (!visibleKeys[k]) return;
        const val = row[k] ?? 0;
        if (!hasData) {
          minVal = val;
          maxVal = val;
          hasData = true;
        } else {
          if (val < minVal) minVal = val;
          if (val > maxVal) maxVal = val;
        }
      });
    });

    if (!hasData || minVal === maxVal) {
      return scaleLinear<number>({
        domain: [0, 1],
        range: [containerHeight, CHART_CONFIG.axis.yPadding],
      });
    }

    if (minVal > 0) minVal = 0;

    return scaleLinear<number>({
      domain: [minVal, maxVal],
      range: [containerHeight, CHART_CONFIG.axis.yPadding],
      nice: true,
    });
  }, [sortedData, keys, visibleKeys, containerHeight]);

  // Bisector for finding closest data point
  const getDate = (d: any) => new Date(d.date).getTime();
  const dateBisector = bisector(getDate).left;

  // Haptic feedback helper
  const triggerHaptic = useCallback(() => {
    if (isWeb) return;
    const now = Date.now();
    if (
      now - lastVibrationTimeRef.current >=
      CHART_CONFIG.interaction.minVibrationInterval
    ) {
      lastVibrationTimeRef.current = now;
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => {
        // Silently handle haptic errors
      });
    }
  }, []);

  // Interpolate data point between two surrounding points
  const interpolateDataPoint = useCallback(
    (xPos: number) => {
      if (!sortedData.length) return null;

      const mouseTime = xScale.invert(xPos).getTime();

      // Check if we're beyond the last meaningful data point
      if (lastMeaningfulDataPoint) {
        const lastMeaningfulTime = getDate(lastMeaningfulDataPoint);

        // If we're beyond the last meaningful point, return its values (no slope down)
        if (mouseTime > lastMeaningfulTime) {
          return {
            date: new Date(mouseTime).toISOString(),
            x: mouseTime,
            ...Object.fromEntries(
              keys.map((key) => [key, lastMeaningfulDataPoint[key] ?? 0])
            ),
          };
        }
      }

      const index = dateBisector(sortedData, mouseTime);

      // If we're at the beginning or end, return the actual data point
      if (index === 0) return sortedData[0];
      if (index >= sortedData.length) return sortedData[sortedData.length - 1];

      const leftPoint = sortedData[index - 1];
      const rightPoint = sortedData[index];

      if (!leftPoint || !rightPoint) return sortedData[index] || sortedData[0];

      const leftTime = getDate(leftPoint);
      const rightTime = getDate(rightPoint);

      // Calculate interpolation factor (0 = left point, 1 = right point)
      const factor = (mouseTime - leftTime) / (rightTime - leftTime);
      const clampedFactor = Math.max(0, Math.min(1, factor));

      // Create interpolated data point
      const interpolatedPoint: any = {
        date: new Date(mouseTime).toISOString(),
        x: mouseTime,
      };

      // Interpolate values for each key
      keys.forEach((key) => {
        const leftValue = leftPoint[key] ?? 0;
        const rightValue = rightPoint[key] ?? 0;
        interpolatedPoint[key] =
          leftValue + (rightValue - leftValue) * clampedFactor;
      });

      return interpolatedPoint;
    },
    [sortedData, xScale, dateBisector, keys, lastMeaningfulDataPoint, getDate]
  );

  // Find closest data point (for tooltip data)
  const findClosestDataPoint = useCallback(
    (xPos: number) => {
      if (!sortedData.length) return null;

      const mouseTime = xScale.invert(xPos).getTime();
      const index = dateBisector(sortedData, mouseTime);

      let closest = sortedData[0];
      let minDistance = Math.abs(mouseTime - getDate(sortedData[0]));

      // Check both sides of the bisector
      const candidates = [
        sortedData[Math.max(0, index - 1)],
        sortedData[Math.min(sortedData.length - 1, index)],
      ].filter(Boolean);

      for (const candidate of candidates) {
        const distance = Math.abs(mouseTime - getDate(candidate));
        if (distance < minDistance) {
          minDistance = distance;
          closest = candidate;
        }
      }

      return closest;
    },
    [sortedData, xScale, dateBisector]
  );

  // Unified interaction handler
  const handleInteraction = useCallback(
    (point: { x: number; y: number } | null, isTouchEvent = false) => {
      if (!point) {
        setInteracting(false);
        setSelectedData(null);
        setInterpolatedData(null);
        hideTooltip();
        lastHoveredDateRef.current = null;
        return;
      }

      // Allow interaction across the full visual extent of the chart
      // Check against the extended boundary if we have meaningful data
      if (sortedData.length > 0 && lastMeaningfulDataPoint) {
        const lastDataPointDate = new Date(getDate(lastMeaningfulDataPoint));
        const timeIncrement = 1000 * 60 * 15; // 15 minutes extension matching AnimatedAreaClosed
        const extendedDate = new Date(
          lastDataPointDate.getTime() + timeIncrement
        );
        const extendedPointX = xScale(extendedDate);

        if (point.x > extendedPointX) {
          setInteracting(false);
          setSelectedData(null);
          setInterpolatedData(null);
          hideTooltip();
          lastHoveredDateRef.current = null;
          return;
        }
      }

      // Get both interpolated data for smooth dots and closest data for tooltip
      const interpolated = interpolateDataPoint(point.x);
      const closest = findClosestDataPoint(point.x);

      if (!interpolated || !closest) return;

      // Always update interpolated data for smooth dots
      setInterpolatedData(interpolated);
      setInteracting(true);

      // Check if all visible values are zero
      const hasNonZeroValue = keys.some((key) => {
        return visibleKeys[key] && closest[key] != null && closest[key] !== 0;
      });

      // Check if this is a new selection for tooltip/haptic feedback
      const newDateString = closest.date;
      const isNewSelection = newDateString !== lastHoveredDateRef.current;

      if (isNewSelection) {
        lastHoveredDateRef.current = newDateString;
        setSelectedData(closest);

        // Haptic feedback for new selections
        if (isTouchEvent) {
          triggerHaptic();
        }
      }

      // Only show tooltip if there are non-zero values
      if (hasNonZeroValue) {
        const tooltipX = point.x;
        showTooltip({
          tooltipLeft: tooltipX,
          tooltipTop: 0,
          tooltipData: closest,
        });
      } else {
        hideTooltip();
      }
    },
    [
      interpolateDataPoint,
      findClosestDataPoint,
      xScale,
      showTooltip,
      hideTooltip,
      triggerHaptic,
    ]
  );

  // Mouse event handlers
  const handleMouseLeave = useCallback(() => {
    setInteracting(false);
    setSelectedData(null);
    setInterpolatedData(null);
    hideTooltip();
    lastHoveredDateRef.current = null;
  }, [hideTooltip]);

  const handleMouseMove = useCallback(
    (evt: React.MouseEvent<SVGRectElement>) => {
      if (!isWeb) return;
      evt.stopPropagation();
      const point = localPoint(evt);

      // Let handleInteraction manage all boundary logic consistently
      handleInteraction(point, false);
    },
    [handleInteraction]
  );

  // Touch event handlers
  const handleTouchEnd = useCallback(() => {
    setInteracting(false);
    setSelectedData(null);
    setInterpolatedData(null);
    hideTooltip();
    lastHoveredDateRef.current = null;
  }, [hideTooltip]);

  const handleTouchStart = useCallback(
    (evt: React.TouchEvent<SVGRectElement>) => {
      evt.preventDefault();
      evt.stopPropagation();
      if (evt.touches.length > 0) {
        const point = localPoint(evt.target as any, evt.touches[0] as any);
        handleInteraction(point, true);
      }
    },
    [handleInteraction]
  );

  const handleTouchMove = useCallback(
    (evt: React.TouchEvent<SVGRectElement>) => {
      evt.preventDefault();
      evt.stopPropagation();
      if (evt.changedTouches.length > 0) {
        const point = localPoint(
          evt.target as any,
          evt.changedTouches[0] as any
        );

        // Let handleInteraction manage all boundary logic consistently
        handleInteraction(point, true);
      }
    },
    [handleInteraction]
  );

  // Legend toggle
  const toggleKeyVisibility = useCallback(
    (key: string) => {
      if (is0ValueData || isNullData) return;

      const visibleCount = Object.values(visibleKeys).filter(Boolean).length;
      if (visibleKeys[key] && visibleCount === 1) return; // Prevent hiding last key

      setVisibleKeys((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    },
    [visibleKeys, is0ValueData, isNullData]
  );

  const generateTickValues = useCallback(() => {
    if (!sortedData.length) return [];

    const numTicks = 4;
    const dates = sortedData.map((d) => new Date(d.date));
    const step = Math.floor(dates.length / (numTicks - 1));

    const ticks: Date[] = [];
    for (let i = 0; i < numTicks - 1; i++) {
      ticks.push(dates[i * step]);
    }
    ticks.push(dates[dates.length - 1]); // Always include last date

    return ticks;
  }, [sortedData]);

  // Color helper
  const getGreyscaleColor = useCallback((color: string) => {
    if (!color?.includes('#')) return color;

    const red = parseInt(color.substr(1, 2), 16);
    const green = parseInt(color.substr(3, 2), 16);
    const blue = parseInt(color.substr(5, 2), 16);
    const grayscale = Math.round(0.299 * red + 0.587 * green + 0.114 * blue);
    const grayscaleHex = grayscale.toString(16).padStart(2, '0');

    return '#' + grayscaleHex + grayscaleHex + grayscaleHex + '60';
  }, []);

  // Initialize visible keys
  useEffect(() => {
    if (keys.length > 0) {
      const init = keys.reduce((acc, k) => {
        acc[k] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setVisibleKeys(init);
    }
  }, [keys]);

  // Handle loading state changes
  useEffect(() => {
    if (LOADING_STATE === 'SUCCESS') {
      setDataLoadCounter((prev) => prev + 1);
    }
  }, [LOADING_STATE]);

  // Update width on container changes
  useEffect(() => {
    if (containerWidth > 0) {
      setWidth(containerWidth - platformConfig.containerPadding);
    }
  }, [containerWidth, platformConfig.containerPadding]);

  // Header title helper
  const getTitleDetail = () => {
    if (!selectedData) return '';
    return formatDate(selectedData.date, selectedTimeSet);
  };

  // Animated hover line component
  const AnimatedHoverLine = ({
    x,
    y1,
    y2,
  }: {
    x: number;
    y1: number;
    y2: number;
  }) => {
    const [spring, api] = useSpring(() => ({
      x1: x,
      x2: x,
      config: { tension: 300, friction: 30 },
    }));

    useEffect(() => {
      api.start({ x1: x, x2: x });
    }, [api, x]);

    const AnimatedLine = animated('line');

    return (
      <AnimatedLine
        {...spring}
        y1={y1}
        y2={y2}
        stroke="white"
        strokeWidth={2}
        pointerEvents="none"
      />
    );
  };

  // Animated dots component
  const AnimatedDot = ({
    keyName,
    color,
    x,
    y,
  }: {
    keyName: string;
    color: string;
    x: number;
    y: number;
  }) => {
    const [spring, api] = useSpring(() => ({
      cx: x,
      cy: y,
      config: { tension: 300, friction: 30 },
    }));

    useEffect(() => {
      api.start({ cx: x, cy: y });
    }, [api, x, y]);

    const AnimatedCircle = animated('circle');

    return (
      <AnimatedCircle
        {...spring}
        r={6}
        fill={color}
        stroke="white"
        strokeWidth={2}
      />
    );
  };

  // Render dots for interpolated data point
  const renderDots = () => {
    if (!interpolatedData || !interacting) return null;

    // Check if all visible values are zero
    const hasNonZeroValue = keys.some((key) => {
      return (
        visibleKeys[key] &&
        interpolatedData[key] != null &&
        interpolatedData[key] !== 0
      );
    });

    if (!hasNonZeroValue) return null;

    return keys
      .filter(
        (key) =>
          visibleKeys[key] &&
          interpolatedData[key] !== null &&
          interpolatedData[key] !== undefined &&
          interpolatedData[key] !== 0
      )
      .map((key, index) => {
        const i = keys.indexOf(key);
        const cx = xScale(
          interpolatedData.x || new Date(interpolatedData.date)
        );
        const cy = yScale(interpolatedData[key]);

        return (
          <AnimatedDot
            key={`dot-${key}`}
            keyName={key}
            color={colors[i]}
            x={cx}
            y={cy}
          />
        );
      });
  };

  return (
    <div
      style={
        !isWeb
          ? {
              position: 'relative',
              width: '100%',
              height: '100%',
            }
          : {}
      }
      ref={containerRef}
    >
      {/* Header */}
      <div style={{ marginBottom: '6px', marginLeft: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {selectedData && (
            <div
              className="heading4"
              style={{ color: '#001117', marginRight: '.5em' }}
            >
              {getTitleDetail()}
            </div>
          )}
          <div
            style={{ color: '#001117' }}
            className={selectedData ? 'heading4L' : 'heading4'}
          >
            {t(title)}
          </div>
        </div>

        {/* Legend */}
        <div style={{ height: '40px' }}>
          {keys.map((k, i) => {
            const isVisible = visibleKeys[k];
            const activeValue =
              isVisible && selectedData && selectedData[k] !== undefined
                ? selectedData[k]
                : undefined;

            return (
              <button
                key={k}
                style={{
                  border: 'none',
                  color: '#505558',
                  display: 'inline-block',
                  borderBottom: `4px solid ${
                    isVisible ? colors[i] : '#F7F7F7'
                  }`,
                  background: 'none',
                  marginRight: '1em',
                  textAlign: 'left',
                  cursor: isNullData || is0ValueData ? 'default' : 'pointer',
                  padding: '0',
                }}
                className="detail"
                onClick={() => toggleKeyVisibility(k)}
              >
                {t(k)}{' '}
                {activeValue !== undefined && interacting ? (
                  <strong style={{ fontWeight: isWebSafari ? 500 : 600 }}>
                    | {activeValue?.toLocaleString(browserLanguage)} {unit}
                  </strong>
                ) : (
                  ''
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <ParentSize>
        {({ width: parentWidth, height: parentHeight }) => (
          <svg
            width={ ENV?.VITE_APP_IS_WEB === 'true' ? parentWidth : innerWidth}
            height={platformConfig.svgHeight}
            style={{
              overflow: 'visible',
              marginTop: platformConfig.headerMarginTop,
            }}
          >
            {/* X Axis */}
            <AxisBottom
              top={(containerHeight + ( isWeb ? 5 : 0 ) )}
              left={margin.left + CHART_CONFIG.axis.bottomLeftOffset}
              scale={xAxisScale}
              numTicks={4}
              tickValues={generateTickValues()}
              stroke="#F7F7F7"
              tickStroke="transparent"
              tickFormat={(value, index, ticks) => {
                // Custom formatting for the last tick based on time range
                if (index === ticks.length - 1) {
                  if (selectedTimeSet === 'day') {
                    return '23:59';
                  } else if (selectedTimeSet === 'year') {
                    return 'Dec';
                  } else {
                    // For other time ranges (week, month, etc.), show the last day/date
                    const date = DateTime.fromJSDate(new Date(value as any));
                    const isoString = date.toISO();
                    return formatDate(isoString || '', selectedTimeSet);
                  }
                }
                const date = DateTime.fromJSDate(new Date(value as any));
                const isoString = date.toISO();
                return formatDate(isoString || '', selectedTimeSet);
              }}
              tickLabelProps={() => ({
                fontSize: 12,
                fill: '#838B93',
                textAnchor: 'middle',
                dx: 0,
              })}
              tickClassName="detail"
            />

            {/* Y Axis */}
            <AxisLeft
              scale={yScale}
              numTicks={is0ValueData || isNullData ? 0 : 4}
              tickFormat={(tick) =>
                tick.toLocaleString(browserLanguage, {
                  maximumFractionDigits: 1,
                })
              }
              stroke="#F7F7F7"
              tickStroke="transparent"
              left={margin.left}
              tickLabelProps={() => ({
                fontSize: 12,
                fill: '#838B93',
                textAnchor: 'end',
                opacity: is0ValueData || isNullData ? 0 : 1,
              })}
              label={unit}
              labelProps={{
                x: 0,
                y: isWeb ? -30 :-10,
                fill: '#838B93',
                fontSize: 12,
                fontWeight: isWebSafari ? 400 : 600,
                textAnchor: 'end',
              }}
              labelClassName={`detail ${styles['rotate']} ${styles['rotate__360']}`}
              tickClassName="detail"
            />

            {/* Areas */}
            {(width > 0 && isNullData) || (width > 0 && is0ValueData) ? (
              // Did some reaerch and foreginObject should not bring issues since it has browser and mobile support with ionic.
              <foreignObject
                x={0}
                y={0}
                width={ENV?.VITE_APP_IS_WEB === 'true' ? parentWidth : (width + margin.left + margin.right)}
                height={containerHeight}
                style={{
                  pointerEvents: 'none', // so the axes remain interactive if needed
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.9,
                  }}
                  className={styles.Areachart__NoData}
                >
                  <IconWrapper
                    iconName={is0ValueData ? 'ellipse' : 'no_data_chart'}
                    style={{ marginBottom: 8 }}
                    className={styles.Areachart__NoData__Icon}
                  />
                  <div className={`${styles.Areachart__NoData__Label} detail`} style={{ width: '70%' }}>
                    {is0ValueData
                      ? t('charts.everything_is_zero')
                      : t('charts.data_unavailable')}
                  </div>
                </div>
              </foreignObject>
            ) : (
              <>
                {keys.map((key, i) => {
                  const keyData = sortedData.map((d) => ({
                    date: d.date,
                    [key]: visibleKeys[key] ? d[key] ?? 0 : 0,
                  }));

                  return (
                    <AnimatedAreaClosed
                      key={`${key}-${dataLoadCounter}`}
                      data={keyData}
                      xScale={xScale}
                      yScale={yScale}
                      accessors={{
                        x: (d: any) => new Date(d.date).getTime(),
                        y: (d: any) => d[key] ?? 0,
                      }}
                      fill={
                        interacting ? getGreyscaleColor(colors[i]) : colors[i]
                      }
                      fillOpacity={0.5}
                      originalData={sortedData}
                      allKeys={keys}
                    />
                  );
                })}

                {/* Hover line and dots */}
                {interpolatedData &&
                  interacting &&
                  (() => {
                    // Check if all visible values are zero
                    const hasNonZeroValue = keys.some((key) => {
                      return (
                        visibleKeys[key] &&
                        interpolatedData[key] != null &&
                        interpolatedData[key] !== 0
                      );
                    });

                    if (!hasNonZeroValue) return null;

                    return (
                      <>
                        <AnimatedHoverLine
                          x={xScale(
                            interpolatedData.x ||
                              new Date(interpolatedData.date)
                          )}
                          y1={CHART_CONFIG.axis.yPadding}
                          y2={containerHeight + 15}
                        />
                        {renderDots()}
                      </>
                    );
                  })()}

                {/* Interaction overlay */}
                <rect
                  x={0}
                  y={0}
                  width={
                    innerWidth + CHART_CONFIG.interaction.overlayPadding.width
                  }
                  height={
                    containerHeight +
                    CHART_CONFIG.interaction.overlayPadding.height
                  }
                  fill="transparent"
                  style={{
                    touchAction: 'none',
                    userSelect: 'none',
                  }}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
              </>
            )}
          </svg>
        )}
      </ParentSize>
    </div>
  );
};

export default NewAreaChart;
